import React, { useState } from 'react';
import { 
  Zap, 
  Key, 
  Send, 
  FileJson, 
  Layers, 
  AlertTriangle, 
  CheckCircle, 
  TestTube,
  Book,
  ChevronDown,
  ChevronRight,
  Webhook,
  Menu,
  X,
  HelpCircle,
  Code,
  Rocket,
  Play,
  Target
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../data/translations';

const Sidebar = ({ activeSection, onSectionChange }) => {
  const { language } = useLanguage();
  
  const menuItems = [
    { id: 'what-is', label: t('navWhatIs', language), icon: Target },
    { id: 'fire-forget', label: t('navFireForget', language), icon: Rocket },
    { id: 'quick-start', label: t('navQuickStart', language), icon: Zap },
    { id: 'sdk-setup', label: t('navSdkSetup', language), icon: Code },
    { id: 'authentication', label: t('navAuthentication', language), icon: Key },
    { id: 'event-types', label: t('navEventTypes', language), icon: Send },
    { id: 'endpoints', label: t('navEndpoints', language), icon: FileJson },
    { 
      id: 'json-formats', 
      label: t('navJsonFormats', language), 
      icon: FileJson,
      children: [
        { id: 'payment-initialized', label: t('navPaymentInitialized', language) },
        { id: 'provider-requested', label: t('navProviderRequested', language) },
        { id: 'provider-success', label: t('navProviderSuccess', language) },
        { id: 'provider-failed', label: t('navProviderFailed', language) },
        { id: 'order-created', label: t('navOrderCreated', language) },
        { id: 'revenue-recorded', label: t('navRevenueRecorded', language) },
        { id: 'refund-initiated', label: t('navRefundInitiated', language) },
        { id: 'refund-completed', label: t('navRefundCompleted', language) },
      ]
    },
    { id: 'batch-events', label: t('navBatchEvents', language), icon: Layers },
    { id: 'error-handling', label: t('navErrorHandling', language), icon: AlertTriangle },
    { id: 'best-practices', label: t('navBestPractices', language), icon: CheckCircle },
    { id: 'testing', label: t('navTesting', language), icon: TestTube },
    { id: 'examples', label: t('navExamples', language), icon: Play },
    { id: 'webhook-integration', label: t('navWebhookIntegration', language), icon: Webhook },
    { id: 'faq', label: t('navFaq', language), icon: HelpCircle },
    { id: 'sdks', label: t('navSdks', language), icon: Book },
  ];
  const [expandedItems, setExpandedItems] = useState(['json-formats']);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleExpand = (id) => {
    setExpandedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleClick = (id, hasChildren) => {
    if (hasChildren) {
      toggleExpand(id);
    } else {
      onSectionChange(id);
      setIsMobileOpen(false);
    }
  };

  const renderMenuItem = (item, depth = 0) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = activeSection === item.id;

    return (
      <div key={item.id}>
        <button
          className={`sidebar-item ${isActive ? 'active' : ''} ${depth > 0 ? 'nested' : ''}`}
          onClick={() => handleClick(item.id, hasChildren)}
          style={{ paddingLeft: `${1 + depth * 0.75}rem` }}
        >
          <div className="sidebar-item-content">
            {Icon && <Icon size={18} />}
            <span>{item.label}</span>
          </div>
          {hasChildren && (
            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
          )}
        </button>
        {hasChildren && isExpanded && (
          <div className="sidebar-children">
            {item.children.map(child => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button 
        className="mobile-menu-toggle" 
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      
      <aside className={`sidebar ${isMobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="logo-text">
              <span className="logo-title">SilentLoss</span>
              <span className="logo-subtitle">Developer Docs</span>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="version-badge">
            <span>{t('apiVersion', language)}</span>
            <span className="version">v1.2</span>
          </div>
        </div>
      </aside>
      
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)} />
      )}
    </>
  );
};

export default Sidebar;
